import { createFileRoute, Link } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdAdd, MdSearch } from 'react-icons/md'
import { useAssistants, useAssistantTemplates, useBotConfig, useDeleteAssistantTemplate } from '~/api'
import { CourseHeading } from '~/components/educator/CourseHeading'
import { AppCard } from '~/components/educator/app/AppCard'
import { CreateApp, createAppDialogOpenAtom } from '~/components/educator/app/CreateApp'
import { templateTags } from '~/model'
import { deleteTemplateIdAtom } from '~/state'
import { Alert, Button, cn, DeleteConfirmationDialog, Input } from '~ui'

export const Route = createFileRoute('/educator/courses/$courseId/apps/')({
	component: Apps,
})

function Apps() {
	const botConfig = useBotConfig()
	const apps = useAssistants()

	const globalApps = apps.filter((app) => app.isGlobal).sort((a, b) => a.name.localeCompare(b.name))
	const yourApps = apps.filter((app) => !app.isGlobal).sort((a, b) => a.createdDatetime?.localeCompare(b.createdDatetime ?? '') ?? 0)

	const [, setCreateAppDialogOpen] = useAtom(createAppDialogOpenAtom)

	return (
		<>
			<CourseHeading>
				<CreateApp />
			</CourseHeading>

			{botConfig?.status !== 'Ready' && (
				<Alert variant="warn">
					Before you can preview apps or templates that use LMS resources, you need to create a
					<Link
						className="pl-[4px] underline"
						from="/educator/courses/$courseId"
						to="/educator/courses/$courseId/data"
					>
						subject dataset.
					</Link>
				</Alert>
			)}

			{/* Global apps */}
			<div className="mb-[32px]">
				<h2 className="text-navy-100 font-600 mb-[4px] text-[20px]">Global apps</h2>
				<p className="mb-[24px]">Pre-built apps made by the University of Melbourne, ready to use or refine for your subject and LMS resources.</p>
				<div className="flex min-h-[213px] flex-col gap-[24px] sm:flex-row sm:flex-wrap">
					{globalApps.map((app) => (
						<AppCard
							key={app.id}
							app={app}
						/>
					))}
				</div>
			</div>

			{/* Subject apps */}
			<div className="mb-[32px]">
				<h2 className="text-navy-100 font-600 mb-[4px] text-[20px]">Subject apps</h2>
				<p className="mb-[24px]">Apps you've created or adapted for use in your subject.</p>
				<div className="flex min-h-[213px] flex-col gap-[24px] sm:flex-row sm:flex-wrap">
					{yourApps.length === 0 ? (
						<Button
							variant="outline"
							className="sm:max-w-card group border-dark-grey-25 flex h-[213px] w-full flex-col items-center justify-center gap-[12px] border bg-white"
							onClick={() => {
								setCreateAppDialogOpen(true)
							}}
						>
							<div className="bg-light-grey-25 flex size-[32px] items-center justify-center group-hover:bg-white group-active:bg-white">
								<MdAdd className="text-navy-100 size-[20px]" />
							</div>
							<div className="font-600 text-[18px]">Create an app</div>
						</Button>
					) : (
						yourApps.map((app) => (
							<AppCard
								key={app.id}
								app={app}
							/>
						))
					)}
				</div>
			</div>

			<TemplatesSection />
		</>
	)
}

const TemplatesSection = () => {
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
	const [yourTemplates, setYourTemplates] = useState<boolean>(false)
	const [search, setSearch] = useState('')
	const { data: allTemplates } = useAssistantTemplates()
	const [deleteTemplateId, setDeleteTemplateId] = useAtom(deleteTemplateIdAtom)
	const { mutate: deleteTemplate } = useDeleteAssistantTemplate()

	const filteredTemplates = useMemo(() => {
		let templates = allTemplates ?? []

		if (selectedCategory) {
			templates = templates.filter((template) => template.tags?.includes(selectedCategory))
		} else if (yourTemplates) {
			templates = templates.filter((template) => template.currentUserIsOwner)
		}

		if (!search.trim()) {
			return templates
		}

		const searchTerms = search
			.trim()
			.toLowerCase()
			.split(' ')
			.map((term) => term.trim())
			.filter((term) => term !== '')

		return templates.filter((template) => {
			const name = template.name.toLowerCase()
			const description = template.description?.toLowerCase() ?? ''

			return searchTerms.every((term) => name.includes(term) || description.includes(term))
		})
	}, [search, selectedCategory, allTemplates, yourTemplates])

	const templateTabsRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (templateTabsRef.current && selectedCategory !== null) {
			templateTabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
		}
	}, [selectedCategory])

	return (
		<div
			ref={templateTabsRef}
			className="max-w-content mb-[32px]"
		>
			<h2
				id="templates"
				className="text-navy-100 font-600 mb-[4px] text-[20px]"
			>
				Templates
			</h2>
			<p className="max-w-text mb-[24px]">Starter templates to help you build new apps. Fully editable. Using a template will clone the app’s configuration and settings, but not its dataset.</p>

			<Alert variant="info">
				If an app template is set to use LMS resources, it will use your subject’s dataset for previews. Since the template was likely created with a different dataset, the prompt may not work as
				intended or produce meaningful results.
			</Alert>

			<div className="mb-[24px] flex flex-wrap items-center justify-between gap-[24px] min-[1456px]:max-w-[1280px] min-[1891px]:max-w-[1715px]">
				{/* Categories tabs */}
				<div className="flex flex-wrap gap-4">
					<button
						type="button"
						onClick={() => {
							setSelectedCategory(null)
							setYourTemplates(false)
						}}
						className={cn(
							'text-navy-100 font-600 [&.active]:border-navy-100 hover:border-navy-50 border-b-[2px] border-transparent pb-[2px] leading-tight',
							selectedCategory == null && !yourTemplates && 'active',
						)}
					>
						All
					</button>
					<button
						type="button"
						onClick={() => {
							setSelectedCategory(null)
							setYourTemplates(true)
						}}
						className={cn(
							'text-navy-100 font-600 [&.active]:border-navy-100 hover:border-navy-50 border-b-[2px] border-transparent pb-[2px] leading-tight',
							selectedCategory == null && yourTemplates && 'active',
						)}
					>
						Your templates
					</button>

					{templateTags.map((category) => (
						<button
							key={category}
							type="button"
							onClick={() => {
								setSelectedCategory(category)
								setYourTemplates(false)
							}}
							className={cn(
								'text-navy-100 font-600 [&.active]:border-navy-100 hover:border-navy-50 border-b-[2px] border-transparent pb-[2px] leading-tight',
								selectedCategory === category && 'active',
							)}
						>
							{category}
						</button>
					))}
				</div>

				{/* Template search */}
				<Input
					placeholder="Search app templates"
					className="h-[32px] w-[263px]"
					value={search}
					icon={<MdSearch className="size-[18px]" />}
					onChange={(e) => {
						setSearch(e.target.value)
					}}
				/>
			</div>

			{/* Templates */}
			<div className="flex min-h-[250px] flex-col gap-[24px] sm:flex-row sm:flex-wrap">
				{filteredTemplates.map((app) => (
					<AppCard
						key={app.id}
						app={app}
					/>
				))}
			</div>

			<DeleteConfirmationDialog
				title="Delete app template"
				open={deleteTemplateId != null}
				onDelete={() => {
					if (deleteTemplateId != null) {
						deleteTemplate(deleteTemplateId)
					}
					setDeleteTemplateId(null)
				}}
				onCancel={() => {
					setDeleteTemplateId(null)
				}}
			>
				This action cannot be undone.
				<br />
				<br />
				Are you sure you want to delete the template?
			</DeleteConfirmationDialog>
		</div>
	)
}
